/**
 * Calendar dates: birthdays, effective dates. Values with no time and no zone.
 *
 * The language parses a date-only string ("2026-01-01") as UTC midnight. Read
 * it back with the ordinary local getters and every user west of UTC lands on
 * the previous day, because local midnight has not happened yet. The project
 * default is `America/Lima` (UTC-5), so this was the common case, not an edge:
 *
 *   new Date('2026-01-01').getFullYear()  // 2025 in Lima
 *   new Date('2026-01-01').getMonth()     // 11 (December)
 *
 * A calendar date has no zone at all - a birthday is the same day everywhere -
 * so the right reading is the one the server wrote. These helpers use the UTC
 * getters to round-trip it exactly.
 */

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** `{year, month, day}` for a calendar date, or null. `month` is 0-based. */
export function calendarDateParts(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return {
        year: parsed.getUTCFullYear(),
        month: parsed.getUTCMonth(),
        day: parsed.getUTCDate(),
    };
}

/** "Jan 2026" for a calendar date, or '' when it cannot be read. */
export function formatEffectiveMonthYear(value) {
    const parts = calendarDateParts(value);
    if (!parts) return '';
    return `${MONTHS[parts.month]} ${parts.year}`;
}

/**
 * Whole years between [dob] and [today], or null when [dob] is unreadable.
 *
 * [today] is the viewer's LOCAL calendar day, which is correct: a birthday
 * arrives when the user's own date says it does, not when UTC agrees.
 */
export function ageFromDob(dob, today = new Date()) {
    const born = calendarDateParts(dob);
    if (!born) return null;

    let age = today.getFullYear() - born.year;
    const monthsApart = today.getMonth() - born.month;
    if (monthsApart < 0 || (monthsApart === 0 && today.getDate() < born.day)) {
        age--;
    }
    return age;
}
